/**
 * BrandFormDialog (Story 40-2 — Epic 40 Brand Management UI)
 *
 * Centered modal Dialog (UI design-system §4.2 — NEVER a Sheet) to create OR
 * edit a Brand. Clones the AddClientSheet scaffold (DialogPrimitive + motion,
 * bg-black/35 overlay, rounded-3xl content, staggered fields). Form state resets
 * on OPEN (so edit loads the brand's values; create starts empty). Validation
 * mirrors the 40-1 server: name required ≤100, owner email valid-or-empty ≤255,
 * logo https-or-empty ≤2048. Toasts surface via the parent-provided showToast.
 */

import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { useBrandMutations, type Brand } from "../../hooks/useBrands";
import type { ToastType } from "../../hooks/useToast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirrors the 40-1 server logoUrlField: a full valid-URL parse + https scheme
 *  (not a prefix match — rejects "https://" / "https:// has spaces"). */
function isValidHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

interface BrandFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  brand?: Brand | null;
  showToast: (type: ToastType, message: string) => void;
  /** Fired with the newly-created brand (create mode only) — lets a caller chain
   *  a follow-up action, e.g. assign the just-created brand to a client. */
  onCreated?: (brand: Brand) => void;
}

export function BrandFormDialog({
  open,
  onOpenChange,
  mode,
  brand,
  showToast,
  onCreated,
}: BrandFormDialogProps) {
  const [name, setName] = useState("");
  const [ownerContactName, setOwnerContactName] = useState("");
  const [ownerContactEmail, setOwnerContactEmail] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { createBrand, updateBrand } = useBrandMutations();
  const pending = createBrand.isPending || updateBrand.isPending;

  // Reset on OPEN (UI directive §4.6) — load brand values for edit, empty for create.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === "edit" && brand) {
      setName(brand.name);
      setOwnerContactName(brand.ownerContactName ?? "");
      setOwnerContactEmail(brand.ownerContactEmail ?? "");
      setLogoUrl(brand.logoUrl ?? "");
    } else {
      setName("");
      setOwnerContactName("");
      setOwnerContactEmail("");
      setLogoUrl("");
    }
  }, [open, mode, brand]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    const trimmedName = name.trim();
    if (!trimmedName) next.name = "Brand name is required";
    else if (trimmedName.length > 100) next.name = "Brand name must be 100 characters or less";
    if (ownerContactEmail.trim() && !EMAIL_RE.test(ownerContactEmail.trim()))
      next.ownerContactEmail = "Invalid email format";
    if (logoUrl.trim() && !isValidHttpsUrl(logoUrl.trim()))
      next.logoUrl = "Logo URL must be a valid https URL";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const values = {
      name: name.trim(),
      ownerContactName: ownerContactName.trim() || null,
      ownerContactEmail: ownerContactEmail.trim() || null,
      logoUrl: logoUrl.trim() || null,
    };
    const onDone = (verb: string) => {
      onOpenChange(false);
      showToast("success", `Brand ${verb}`);
    };
    const onFail = (err: unknown) =>
      showToast("error", (err as { message?: string })?.message ?? "Something went wrong");

    if (mode === "edit" && brand) {
      updateBrand.mutate({ id: brand.id, ...values }, { onSuccess: () => onDone("updated"), onError: onFail });
    } else {
      createBrand.mutate(values, {
        onSuccess: (created) => {
          onDone("created");
          onCreated?.(created);
        },
        onError: onFail,
      });
    }
  }

  // §4.2: don't let Escape / overlay-click tear down the modal mid-request.
  function handleOpenChange(next: boolean) {
    if (!next && pending) return;
    onOpenChange(next);
  }

  const title = mode === "edit" ? "Edit Brand" : "New Brand";
  const description =
    mode === "edit"
      ? "Update this brand's details and owner contact."
      : "Create a brand to group clients (locations / sub-brands) under one owner.";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {title}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                </div>

                <form onSubmit={handleSubmit} noValidate className="space-y-4 mt-6">
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <label htmlFor="brand-name" className="text-sm font-medium">
                      Brand Name <span className="text-destructive">*</span>
                      {errors.name && (
                        <span className="text-sm text-destructive font-normal ml-1" role="alert">
                          {errors.name}
                        </span>
                      )}
                    </label>
                    <Input
                      id="brand-name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) setErrors((p) => ({ ...p, name: "" }));
                      }}
                      placeholder="e.g. North Region"
                      className={`rounded-lg ${errors.name ? "border-destructive" : ""}`}
                      maxLength={100}
                    />
                  </motion.div>

                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <label htmlFor="brand-owner-name" className="text-sm font-medium">
                      Owner Contact Name
                    </label>
                    <Input
                      id="brand-owner-name"
                      value={ownerContactName}
                      onChange={(e) => setOwnerContactName(e.target.value)}
                      placeholder="e.g. Jane Owner"
                      maxLength={255}
                      className="rounded-lg"
                    />
                  </motion.div>

                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <label htmlFor="brand-owner-email" className="text-sm font-medium">
                      Owner Contact Email
                    </label>
                    <Input
                      id="brand-owner-email"
                      type="email"
                      value={ownerContactEmail}
                      onChange={(e) => {
                        setOwnerContactEmail(e.target.value);
                        if (errors.ownerContactEmail)
                          setErrors((p) => ({ ...p, ownerContactEmail: "" }));
                      }}
                      placeholder="e.g. owner@brand.com"
                      maxLength={255}
                      className={`rounded-lg ${errors.ownerContactEmail ? "border-destructive" : ""}`}
                    />
                    {errors.ownerContactEmail && (
                      <p className="text-sm text-destructive" role="alert">
                        {errors.ownerContactEmail}
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Setting an owner email lets you share this brand's roll-ups with them.
                    </p>
                  </motion.div>

                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <label htmlFor="brand-logo" className="text-sm font-medium">
                      Logo URL
                    </label>
                    <Input
                      id="brand-logo"
                      value={logoUrl}
                      onChange={(e) => {
                        setLogoUrl(e.target.value);
                        if (errors.logoUrl) setErrors((p) => ({ ...p, logoUrl: "" }));
                      }}
                      placeholder="https://…"
                      maxLength={2048}
                      className={`rounded-lg ${errors.logoUrl ? "border-destructive" : ""}`}
                    />
                    {errors.logoUrl && (
                      <p className="text-sm text-destructive" role="alert">
                        {errors.logoUrl}
                      </p>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                        style={{ border: "0.5px solid hsl(var(--input))" }}
                        onClick={() => handleOpenChange(false)}
                        disabled={pending}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="brand"
                        className="rounded-xl cursor-pointer"
                        disabled={!name.trim() || pending}
                      >
                        {pending
                          ? mode === "edit"
                            ? "Saving..."
                            : "Creating..."
                          : mode === "edit"
                            ? "Save Changes"
                            : "Create Brand"}
                      </Button>
                    </div>
                  </motion.div>
                </form>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
